import axios from 'axios';

// Frontend and backend are always served from the same origin (both in the
// preview environment and in production). Using window.location.origin makes
// API calls immune to www/apex custom-domain mismatches and cross-origin/CORS
// redirect failures.
const BACKEND_URL = window.location.origin;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  
  // Don't set Content-Type for FormData - let axios handle it
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  getMsConfig: () => api.get('/auth/ms-config'),
  msLogin: (data) => api.post('/auth/ms/login', data),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getManagers: () => api.get('/users/managers'),
  updateRole: (id, role) => api.patch(`/users/${id}/role`, { role }),
  updateManager: (id, managerId) => api.patch(`/users/${id}/manager`, { reporting_manager_id: managerId }),
};

// User API (with access management)
export const userAPI = {
  getAll: () => api.get('/users'),
  getManagers: () => api.get('/users/managers'),
  updateRole: (id, role) => api.patch(`/users/${id}/role`, { role }),
  updateManager: (id, managerId) => api.patch(`/users/${id}/manager`, { reporting_manager_id: managerId }),
  updateAccess: (id, data) => api.patch(`/users/${id}/access`, data),
  getAccess: (id) => api.get(`/users/${id}/access`),
  cleanupInactive: () => api.delete('/users/cleanup-inactive'),
  removeNonDomainUsers: (domain) => api.delete('/users/cleanup-non-domain', { params: { domain } }),
};

// Employee API
// Performance & Discipline Record API (HR + admin only)
export const disciplineAPI = {
  getOverview: () => api.get('/hrm/discipline/overview'),
  getRecord: (employeeId) => api.get(`/hrm/discipline/record/${employeeId}`),
  createWarning: (body) => api.post('/hrm/discipline/warnings', body),
  voidWarning: (id, reason) => api.post(`/hrm/discipline/warnings/${id}/void`, { reason }),
};

export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  getMe: () => api.get('/employees/me'),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  getPayroll: (id, month, year) => api.get(`/employees/${id}/payroll`, { params: { month, year } }),
  // Employee confirmation (triggers EL credit)
  confirm: (id) => api.post(`/employees/${id}/confirm`),
  // Get employee types and statuses
  getTypes: () => api.get('/employees/types/list'),
};

// Payroll API (Employee Self-Service)
export const payrollAPI = {
  getMyPayroll: (params) => api.get('/payroll/me', { params }),
  downloadPayslip: (month, year) => api.get(`/payroll/me/payslip/${month}/${year}`, { responseType: 'blob' }),
};

// Payroll AI API
export const payrollAIAPI = {
  compute: (empId, data) => api.post(`/payroll/ai/compute/${empId}`, data),
  getSuggestion: (empId, month, year) => api.get(`/payroll/ai/suggestion/${empId}`, { params: { month, year } }),
  listComputations: (params) => api.get('/payroll/ai/computations', { params }),
  override: (computationId, data) => api.put(`/payroll/ai/override/${computationId}`, data),
  accept: (computationId) => api.post(`/payroll/ai/accept/${computationId}`),
  lock: (computationId) => api.post(`/payroll/ai/lock/${computationId}`),
  generateForm16: (empId, fiscalYear) => api.get(`/payroll/ai/form16/${empId}`, { params: { fiscal_year: fiscalYear } }),
};

// Holiday API
export const holidayAPI = {
  getAll: (params) => api.get('/holidays', { params }),
  create: (data) => api.post('/holidays', data),
  delete: (id) => api.delete(`/holidays/${id}`),
};

// Guest API
export const guestAPI = {
  getAvailableAssets: () => api.get('/guest/assets'),
};

// Leave API with EL (Earned Leave) only system
export const leaveAPI = {
  getAll: (params) => api.get('/leaves', { params }),
  getMyTeam: () => api.get('/leaves', { params: { my_team: true } }),
  getAllPending: () => api.get('/leaves', { params: { all_pending: true } }),
  create: (data) => api.post('/leaves', data),
  managerApprove: (id, action, reason) => api.post(`/leaves/${id}/manager-approve`, { action, reason }),
  finalApprove: (id, action, reason) => api.post(`/leaves/${id}/final-approve`, { action, reason }),
  exceptionalApprove: (id, action, reason) => api.post(`/leaves/${id}/exceptional-approve`, { action, reason }),
  getBalance: (employeeId, year) => api.get(`/leaves/balance/${employeeId}`, { params: { year } }),
  getMyBalance: (year) => api.get('/leaves/balance/me', { params: { year } }),
  // Leave Calendar
  getCalendar: (params) => api.get('/leaves/calendar', { params }),
  // EL Credit (HR/Admin)
  creditEL: (employeeId, quarter, year) => api.post(`/leaves/el/credit/${employeeId}`, null, { params: { quarter, year } }),
  creditELOnConfirmation: (employeeId) => api.post(`/leaves/el/credit-on-confirmation/${employeeId}`),
  runQuarterlyCredit: () => api.post('/leaves/el/run-quarterly-credit'),
  yearEndRollover: (year) => api.post('/leaves/el/year-end-rollover', null, { params: { year } }),
  // Migration
  initializeBalances: (year, clearOld) => api.post('/leaves/migrate/initialize-balances', null, { params: { year, clear_old: clearOld } }),
  // EL Encashment
  encashEL: (employeeId, days, notes) => api.post('/leaves/el/encash', null, { params: { employee_id: employeeId, days, notes } }),
  getEncashments: (params) => api.get('/leaves/el/encashments', { params }),
  approveEncashment: (id) => api.put(`/leaves/el/encashments/${id}/approve`),
  markEncashmentPaid: (id, paymentReference) => api.put(`/leaves/el/encashments/${id}/mark-paid`, null, { params: { payment_reference: paymentReference } }),
  getPendingForPayslip: (year) => api.get('/leaves/el/encashments/pending-for-payslip', { params: { year } }),
  // Credit History
  getCreditHistory: (employeeId, year) => api.get(`/leaves/el/credit-history/${employeeId}`, { params: { year } }),
};

// Expense API with 3-level approval
export const expenseAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getMyTeam: () => api.get('/expenses', { params: { my_team: true } }),
  getAllPending: () => api.get('/expenses', { params: { all_pending: true } }),
  create: (data) => api.post('/expenses', data),
  managerApprove: (id, action, reason) => api.post(`/expenses/${id}/manager-approve`, { action, reason }),
  finalApprove: (id, action, reason) => api.post(`/expenses/${id}/final-approve`, { action, reason }),
  exceptionalApprove: (id, action, reason) => api.post(`/expenses/${id}/exceptional-approve`, { action, reason }),
  markPaid: (id) => api.post(`/expenses/${id}/mark-paid`),
};

// Asset API
export const assetAPI = {
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  get: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.put(`/assets/${id}`, data),
  assign: (id, employeeId, sendNotification = true) => api.patch(`/assets/${id}/assign`, { employee_id: employeeId }, { params: { send_notification: sendNotification } }),
  unassign: (id) => api.patch(`/assets/${id}/assign`, { employee_id: null }),
  delete: (id) => api.delete(`/assets/${id}`),
  // Invoice upload - using multipart form data
  uploadInvoice: (id, formData) => api.post(`/assets/${id}/upload-invoice`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getInvoice: (id) => api.get(`/assets/${id}/invoice`, { responseType: 'blob' }),
  deleteInvoice: (id) => api.delete(`/assets/${id}/invoice`),
  // Asset Allocation
  getAllocations: (params) => api.get('/assets/allocations', { params }),
  getEmployeeAllocations: (employeeId) => api.get(`/assets/allocations/${employeeId}/history`),
  getAllocationTemplate: () => api.get('/assets/allocation/template'),
  updateAllocationTemplate: (data) => api.put('/assets/allocation/template', data),
  // Public allocation actions (no auth)
  verifyAllocationToken: (token) => api.get('/assets/allocation/verify-token', { params: { token } }),
  executeAllocationAction: (token, remarks = '') => api.post('/assets/allocation/execute', null, { params: { token, remarks } }),
  // Allocation Reminders
  getAllocationReminderSummary: () => api.get('/assets/allocation-reminders/summary'),
  sendAllocationReminders: () => api.post('/assets/allocation-reminders/send'),
};

// CRM API
export const crmAPI = {
  getLeads: (params) => api.get('/crm/leads', { params }),
  getLead: (id) => api.get(`/crm/leads/${id}`),
  createLead: (data) => api.post('/crm/leads', data),
  updateLead: (id, data) => api.put(`/crm/leads/${id}`, data),
  deleteLead: (id) => api.delete(`/crm/leads/${id}`),
  updateRemarks: (id, remarks) => api.put(`/crm/leads/${id}`, { remarks }),
  getActivities: (leadId) => api.get(`/crm/leads/${leadId}/activities`),
  createActivity: (leadId, data) => api.post(`/crm/leads/${leadId}/activities`, data),
  // Aging
  getAgingSummary: () => api.get('/crm/leads/aging-summary'),
  getAgingReport: () => api.get('/crm/aging/report'),
  triggerAgingAlerts: () => api.post('/crm/aging/send-alerts'),
  // Import - let axios set Content-Type automatically for FormData
  importLeads: (formData) => api.post('/crm/leads/import', formData),
  getImportTemplate: () => api.get('/crm/leads/import/template'),
  // Dashboard
  getDashboard: () => api.get('/crm/dashboard/stats'),
  // Follow-ups
  getMyFollowUps: (params) => api.get('/crm/follow-ups/my', { params }),
  getFollowUpsSummary: () => api.get('/crm/follow-ups/summary'),
  // Soft delete and restore
  getDeletedLeads: () => api.get('/crm/leads/deleted'),
  restoreLead: (id) => api.post(`/crm/leads/${id}/restore`),
};

// RBAC API
export const rbacAPI = {
  getModules: () => api.get('/rbac/modules'),
  getRoles: () => api.get('/rbac/roles'),
  getRolePermissions: (role) => api.get(`/rbac/roles/${role}`),
  updateRolePermissions: (role, data) => api.put(`/rbac/roles/${role}`, data),
  resetRolePermissions: (role) => api.post(`/rbac/roles/${role}/reset`),
  getMyPermissions: () => api.get('/rbac/my-permissions'),
  cleanupOverrides: (dryRun) => api.post(`/rbac/cleanup-overrides?dry_run=${dryRun}`),
  getAccessLog: (params = {}) => api.get('/rbac/access-log', { params }),
};

// Meta Lead Ads API (Performance Engineer / Admin validation queue)
export const metaLeadsAPI = {
  getStatus: () => api.get('/crm/meta/status'),
  sync: () => api.post('/crm/meta/sync'),
  getLeads: (state = 'pending') => api.get('/crm/meta/leads', { params: { state } }),
  approve: (id, assignTo = null) => api.post(`/crm/meta/leads/${id}/approve`, { assign_to: assignTo }),
  reject: (id, reason) => api.post(`/crm/meta/leads/${id}/reject`, { reason }),
  getSalesUsers: () => api.get('/crm/meta/sales-users'),
  getCampaignQuality: () => api.get('/crm/meta/campaign-quality'),
  getCampaignReach: (days = 30) => api.get('/crm/meta/campaign-reach', { params: { days } }),
  getInsights: () => api.get('/crm/meta/insights'),
  syncStage: (id) => api.post(`/crm/meta/leads/${id}/sync-stage`),
  sendRejectionDigest: () => api.post('/crm/meta/rejection-digest/send'),
  getConfig: () => api.get('/crm/meta/config'),
  saveConfig: (accounts) => api.put('/crm/meta/config', { accounts }),
};

// WhatsApp API
export const whatsappAPI = {
  getConfig: () => api.get('/integrations/whatsapp/config'),
  saveConfig: (data) => api.post('/integrations/whatsapp/config', data),
  sendMessage: (data) => api.post('/integrations/whatsapp/send', data),
};

// DevOps API
export const devopsAPI = {
  getProjects: () => api.get('/devops/projects'),
  getWorkItems: (projectId, top = 50) => api.get(`/devops/work-items/${encodeURIComponent(projectId)}`, { params: { top } }),
  getAllWorkItems: (params) => api.get('/devops/all-work-items', { params }),
  getTasks: (params) => api.get('/devops/tasks', { params }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAnalytics: () => api.get('/dashboard/analytics'),
  downloadReport: (period = 'monthly') => api.get('/dashboard/report/pdf', { params: { period }, responseType: 'blob' }),
};

// Travel API
export const travelAPI = {
  // CRUD
  getAll: (params) => api.get('/travel/requests', { params }),
  getById: (id) => api.get(`/travel/requests/${id}`),
  create: (data) => api.post('/travel/requests', data),
  update: (id, data) => api.put(`/travel/requests/${id}`, data),
  delete: (id) => api.delete(`/travel/requests/${id}`),
  
  // Workflow
  submit: (id) => api.post(`/travel/requests/${id}/submit`),
  cancel: (id, comments) => api.post(`/travel/requests/${id}/cancel`, { comments }),
  
  // Approvals
  getPending: () => api.get('/travel/requests/pending'),
  approveL1: (id, comments) => api.post(`/travel/requests/${id}/approve/l1`, { comments }),
  rejectL1: (id, comments) => api.post(`/travel/requests/${id}/reject/l1`, { comments }),
  approveL2: (id, comments) => api.post(`/travel/requests/${id}/approve/l2`, { comments }),
  rejectL2: (id, comments) => api.post(`/travel/requests/${id}/reject/l2`, { comments }),
  approveL3: (id, comments) => api.post(`/travel/requests/${id}/approve/l3`, { comments }),
  rejectL3: (id, comments) => api.post(`/travel/requests/${id}/reject/l3`, { comments }),
  exceptionalApprove: (id, action, comments) => api.post(`/travel/requests/${id}/exceptional-approve`, { action, comments }),
  
  // Advance
  releaseAdvance: (id, amount, comments) => api.post(`/travel/requests/${id}/advance/release`, { amount, comments }),
  
  // Approver config
  getApprovers: () => api.get('/travel/approvers'),
  setApprovers: (department, l2Id, l3Id) => api.put(`/travel/approvers/${department}`, { l2_approver_id: l2Id, l3_approver_id: l3Id }),
};

// Timesheet API
export const timesheetAPI = {
  // Week data
  getWeek: (startDate, employeeId = null, skipDevops = false) => api.get('/timesheet/week', { params: { start_date: startDate, employee_id: employeeId, skip_devops: skipDevops } }),
  
  // Projects
  getProjects: () => api.get('/timesheet/projects'),
  createProject: (data) => api.post('/timesheet/projects', data),
  
  // DevOps tasks
  getDevOpsTasks: (project = null, search = null) => api.get('/timesheet/devops/tasks', { params: { project, search } }),
  getCompletedDevOpsTasks: (weekStart) => api.get('/timesheet/devops/completed', { params: { week_start: weekStart } }),
  syncFromDevOps: (weekStart) => api.post('/timesheet/sync-from-devops', null, { params: { week_start: weekStart } }),
  
  // Entries
  createEntry: (data) => api.post('/timesheet/entry', data),
  updateEntry: (id, data) => api.post('/timesheet/entry', data, { params: { entry_id: id } }),
  deleteEntry: (id) => api.delete(`/timesheet/entry/${id}`),
  
  // Week workflow
  submitWeek: (weekStart) => api.post('/timesheet/submit-week', { week_start: weekStart }),
  approveWeek: (weekStart, comments = null) => api.post('/timesheet/approve-week', { week_start: weekStart, comments }),
  rejectWeek: (weekStart, reason) => api.post('/timesheet/reject-week', { week_start: weekStart, reason }),
  
  // Approvals
  getPendingApprovals: () => api.get('/timesheet/pending-approvals'),
  
  // Reports
  getMySummary: (range = 'this_month') => api.get('/timesheet/reports/my-summary', { params: { range } }),
  getManagerSummary: (range = 'this_month') => api.get('/timesheet/reports/manager-summary', { params: { range } }),
  
  // AI Summary
  getAISummary: (data) => api.post('/timesheet/ai/summary', data),
  sendReport: (date, reportText) => api.post('/timesheet/ai/send-report', { date, final_report_text: reportText }),
  
  // Email settings
  getEmailSettings: () => api.get('/timesheet/settings/email'),
  saveEmailSettings: (data) => api.post('/timesheet/settings/email', data),
  testEmail: () => api.post('/timesheet/settings/email/test'),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationIds) => api.post('/notifications/mark-read', { notification_ids: notificationIds }),
  markAllAsRead: (module = null) => api.post('/notifications/mark-all-read', null, { params: { module } }),
  archive: (notificationIds) => api.post('/notifications/archive', { notification_ids: notificationIds }),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Audit API (Super Admin only)
export const auditAPI = {
  getLogs: (params) => api.get('/audit/logs', { params }),
  getLog: (id) => api.get(`/audit/logs/${id}`),
  getEntityHistory: (module, entityId) => api.get(`/audit/entity/${module}/${entityId}/history`),
  getUserActivity: (userId, params) => api.get(`/audit/user/${userId}/activity`, { params }),
  getStats: (period = 'week') => api.get('/audit/stats', { params: { period } }),
  exportLogs: (data) => api.post('/audit/export', data),
};

// Error Logs API (Admin only)
export const errorLogsAPI = {
  getAll: (params) => api.get('/errors', { params }),
  getById: (id) => api.get(`/errors/${id}`),
  getDashboard: (period = 'week') => api.get('/errors/dashboard', { params: { period } }),
  report: (data) => api.post('/errors/report', data),
  assign: (id, adminUserId) => api.post(`/errors/${id}/assign`, null, { params: { admin_user_id: adminUserId } }),
  resolve: (id, data) => api.post(`/errors/${id}/resolve`, data),
  analyzeWithAI: (id) => api.post(`/errors/${id}/ai-analyze`),
  submitAIFeedback: (id, data) => api.post(`/errors/${id}/ai-feedback`, data),
};

// Attendance API
export const attendanceAPI = {
  // CSV Import
  importCSV: (formData) => api.post('/attendance/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getImportHistory: (limit = 20) => api.get('/attendance/import/history', { params: { limit } }),
  getImportTemplate: () => api.get('/attendance/import/template'),
  
  // Excel Import (Biometric System)
  importExcel: (formData, autoDeductLeave = true) => api.post('/attendance/import/excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: { auto_deduct_leave: autoDeductLeave }
  }),
  previewExcel: (formData) => api.post('/attendance/import/excel/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getExcelImportHistory: (limit = 20) => api.get('/attendance/import/excel/history', { params: { limit } }),
  
  // Attendance Records
  getRecords: (params) => api.get('/attendance/records', { params }),
  getRecordSummary: (employeeId, startDate, endDate) => api.get(`/attendance/records/summary/${employeeId}`, { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  getBillablePercentage: (employeeId, startDate, endDate) => api.get(`/attendance/records/billable/${employeeId}`, {
    params: { start_date: startDate, end_date: endDate }
  }),
  
  // Attendance Days (Legacy)
  getDays: (params) => api.get('/attendance/days', { params }),
  getSummary: (params) => api.get('/attendance/summary', { params }),
  createRecord: (data) => api.post('/attendance/days', null, { params: data }),
  deleteRecord: (id) => api.delete(`/attendance/days/${id}`),
  
  // AI Insights
  generateInsights: (employeeId, from, to, refresh = false) => 
    api.post(`/attendance/ai/insights?employee_id=${employeeId}&from=${from}&to=${to}&refresh=${refresh}`),
  
  // Team & Org Summaries
  getTeamSummary: (from, to) => api.get('/attendance/team-summary', { params: { from, to } }),
  getOrgSummary: (from, to, department) => api.get('/attendance/org-summary', { params: { from, to, department } }),
  
  // My Stats
  getMyStats: () => api.get('/attendance/my-stats'),
  
  // Exception Management (HR → Super Admin approval)
  createException: (data) => api.post('/attendance/exceptions', data),
  getExceptions: (params) => api.get('/attendance/exceptions', { params }),
  getPendingExceptions: () => api.get('/attendance/exceptions/pending'),
  getEmployeeExceptions: (employeeId) => api.get(`/attendance/exceptions/employee/${employeeId}`),
  approveException: (exceptionId, comments) => api.post(`/attendance/exceptions/${exceptionId}/approve`, { comments }),
  rejectException: (exceptionId, reason) => api.post(`/attendance/exceptions/${exceptionId}/reject`, { reason }),
  
  // Compensation Hours
  processCompensation: (employeeId, month) => api.post(`/attendance/compensation/process/${employeeId}`, null, { params: { month } }),
  runMonthlyCompensation: () => api.post('/attendance/compensation/run-monthly'),
  getCompensationHistory: (employeeId) => api.get(`/attendance/compensation/history/${employeeId}`),
  
  // Leave Adjustments (attendance-based)
  getLeaveAdjustments: (employeeId, year) => api.get(`/attendance/leave-adjustments/${employeeId}`, { params: { year } }),
  
  // Deductions
  getDeductions: (params) => api.get('/attendance/deductions', { params }),
  getEmployeeDeductions: (employeeId, status) => api.get(`/attendance/deductions/${employeeId}`, { params: { status } }),
  
  // Daily Alerts
  sendDailyAlerts: () => api.post('/attendance/alerts/send-daily'),
  
  // AI Stats (Employee Dashboard)
  getAIStats: (employeeId, days = 30) => api.get(`/attendance/ai-stats/${employeeId}`, { params: { days } }),
  getMyAIStats: (days = 30) => api.get('/attendance/ai-stats/my', { params: { days } }),
  
  // Configuration (Super Admin)
  getConfig: () => api.get('/attendance/config'),
  updateConfig: (data) => api.put('/attendance/config', data),
  getConfigHistory: (limit = 20) => api.get('/attendance/config/history', { params: { limit } }),
  
  // Regularization (Employee)
  createRegularizationRequest: (data) => api.post('/attendance/regularization/request', data),
  getMyRegularizationRequests: (params) => api.get('/attendance/regularization/my-requests', { params }),
  
  // Regularization (HR)
  getPendingHRRequests: () => api.get('/attendance/regularization/pending-hr'),
  hrApproveRequest: (requestId, data) => api.put(`/attendance/regularization/${requestId}/hr-approve`, data),
  hrRejectRequest: (requestId, data) => api.put(`/attendance/regularization/${requestId}/hr-reject`, data),
  
  // Regularization (Super Admin)
  getPendingAdminRequests: () => api.get('/attendance/regularization/pending-admin'),
  adminApproveRequest: (requestId, data) => api.put(`/attendance/regularization/${requestId}/admin-approve`, data),
  adminRejectRequest: (requestId, data) => api.put(`/attendance/regularization/${requestId}/admin-reject`, data),
  
  // All Regularizations (Admin view)
  getAllRegularizationRequests: (params) => api.get('/attendance/regularization/all', { params }),
  
  // Digest
  getMyDigests: (limit = 7) => api.get('/attendance/digest/my', { params: { limit } }),
  getTodayDigest: () => api.get('/attendance/digest/today'),
  getMonthlyDigest: (year, month) => api.get('/attendance/digest/monthly', { params: { year, month } }),
  markDigestRead: (digestId) => api.put(`/attendance/digest/${digestId}/read`),
  triggerDailyDigest: () => api.post('/attendance/digest/send-now'),
  
  // Attendance Issues (Single Punch, etc.)
  getIssuesSummary: (from, to, department) => api.get('/attendance/issues/summary', { params: { from, to, department } }),
  getEmployeeIssues: (employeeId, from, to) => api.get(`/attendance/issues/employee/${employeeId}`, { params: { from, to } }),
};

// Billing/Invoice API
export const billingAPI = {
  // Projects from DevOps
  getProjectsFromDevOps: () => api.get('/billing/projects-from-devops'),
  
  // Project invoice configuration
  getProjectConfig: (epicId) => api.get('/billing/project-config', { params: { epic_id: epicId } }),
  saveProjectConfig: (data) => api.post('/billing/project-config', data),
  
  // Invoices CRUD
  getInvoices: (params) => api.get('/billing/invoices', { params }),
  getInvoice: (id) => api.get(`/billing/invoices/${id}`),
  createInvoice: (data) => api.post('/billing/invoices', data),
  updateInvoice: (id, data) => api.put(`/billing/invoices/${id}`, data),
  deleteInvoice: (id) => api.delete(`/billing/invoices/${id}`),
  
  // Invoice actions
  downloadInvoicePDF: (id) => api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' }),
  sendInvoice: (id, additionalRecipients = []) => api.post(`/billing/invoices/${id}/generate-and-send`, { additional_recipients: additionalRecipients }),
  markPaid: (id, data) => api.post(`/billing/invoices/${id}/mark-paid`, null, { params: data }),
  cancelInvoice: (id, reason) => api.post(`/billing/invoices/${id}/cancel`, null, { params: { reason } }),
  
  // Payments
  getPaymentConfig: () => api.get('/billing/payments/config'),
  getPayableInvoices: (customerEmail) => api.get('/billing/payments/invoices', { params: { customer_email: customerEmail } }),
  getBankDetails: () => api.get('/billing/bank-details'),
  
  // Razorpay
  createRazorpayOrder: (data) => api.post('/billing/payments/razorpay/order', data),
  verifyRazorpayPayment: (data) => api.post('/billing/payments/razorpay/verify', data),
  
  // Reminders
  runReminders: () => api.post('/billing/run-reminders'),
  
  // Settings (Super Admin)
  getRazorpaySettings: () => api.get('/billing/settings/razorpay'),
  saveRazorpaySettings: (data) => api.post('/billing/settings/razorpay', data),
  getBankSettings: () => api.get('/billing/settings/bank-details'),
  saveBankSettings: (data) => api.post('/billing/settings/bank-details', data),
};

// Public Invoice API (no auth)
export const publicInvoiceAPI = {
  getInvoice: (token) => api.get(`/public/invoice/${token}`),
  downloadPDF: (token) => api.get(`/public/invoice/${token}/pdf`, { responseType: 'blob' }),
  createPaymentOrder: (token) => api.post(`/public/invoice/${token}/pay`),
  verifyPayment: (token, data) => api.post(`/public/invoice/${token}/verify-payment`, data),
};

// AD Sync API
export const adSyncAPI = {
  // Status & Stats
  getStatus: () => api.get('/ad-sync/status'),
  
  // Search AD Users
  searchUsers: (query, limit = 20) => api.get('/ad-sync/search', { params: { q: query, limit } }),
  getUserDetails: (objectId) => api.get(`/ad-sync/user/${objectId}`),
  
  // Import
  importUser: (data) => api.post('/ad-sync/import', data),
  
  // Bulk Sync
  previewBulkSync: (includeDisabled = true) => api.get('/ad-sync/preview', { params: { include_disabled: includeDisabled } }),
  executeBulkSync: (previewOnly = false, includeDisabled = true) => 
    api.post('/ad-sync/bulk-sync', { preview_only: previewOnly, include_disabled: includeDisabled }),
  
  // Refresh single employee from AD
  refreshEmployee: (employeeId) => api.post(`/ad-sync/refresh-user/${employeeId}`),
  
  // Re-link managers from AD
  relinkManagers: () => api.post('/ad-sync/relink-managers'),
  
  // Logs
  getLogs: (limit = 50, operationType = null) => api.get('/ad-sync/logs', { params: { limit, operation_type: operationType } }),
};

// Recruitment APIs
export const recruitmentAPI = {
  // Requisitions
  getRequisitions: (params) => api.get('/recruitment/requisitions', { params }),
  getRequisition: (id) => api.get(`/recruitment/requisitions/${id}`),
  createRequisition: (data) => api.post('/recruitment/requisitions', data),
  updateRequisition: (id, data) => api.put(`/recruitment/requisitions/${id}`, data),
  deleteRequisition: (id) => api.delete(`/recruitment/requisitions/${id}`),
  
  // Candidates
  getCandidates: (params) => api.get('/recruitment/candidates', { params }),
  getCandidate: (id) => api.get(`/recruitment/candidates/${id}`),
  createCandidate: (data) => api.post('/recruitment/candidates', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateCandidate: (id, data) => api.put(`/recruitment/candidates/${id}`, data),
  deleteCandidate: (id) => api.delete(`/recruitment/candidates/${id}`),
  updateCandidateStatus: (id, status) => api.patch(`/recruitment/candidates/${id}/status`, { status }),
  
  // Applications
  getApplications: (params) => api.get('/recruitment/applications', { params }),
  createApplication: (data) => api.post('/recruitment/applications', data),
  updateApplicationStatus: (id, status, notes) => api.patch(`/recruitment/applications/${id}/status`, { status, notes }),
  
  // Offers
  getOffers: (params) => api.get('/recruitment/offers', { params }),
  getOffer: (id) => api.get(`/recruitment/offers/${id}`),
  createOffer: (data) => api.post('/recruitment/offers', data),
  updateOffer: (id, data) => api.put(`/recruitment/offers/${id}`, data),
  getPendingApprovals: () => api.get('/recruitment/offers/pending-approvals'),
  approveOffer: (id, action, comments) => api.post(`/recruitment/offers/${id}/approve`, { action, comments }),
  recordCandidateDecision: (id, decision, notes) => api.post(`/recruitment/offers/${id}/candidate-decision`, { candidate_decision: decision, notes }),
};

// GST / Accounts API
export const gstAPI = {
  // Config
  getConfig: () => api.get('/accounts/gst/config'),
  saveConfig: (data) => api.post('/accounts/gst/config', data),
  getStates: () => api.get('/accounts/gst/states'),
  getRates: () => api.get('/accounts/gst/rates'),
  
  // SAC Codes
  getSACCodes: (params) => api.get('/accounts/gst/sac-codes', { params }),
  createSACCode: (data) => api.post('/accounts/gst/sac-codes', data),
  
  // Calculation
  calculateGST: (baseAmount, gstRate, supplierState, recipientState) => 
    api.post('/accounts/gst/calculate', null, { 
      params: { base_amount: baseAmount, gst_rate: gstRate, supplier_state: supplierState, recipient_state: recipientState } 
    }),
  
  // Input Tax Credit (ITC)
  getITCEntries: (params) => api.get('/accounts/gst/itc', { params }),
  createITCEntry: (data) => api.post('/accounts/gst/itc', data),
  claimITC: (id) => api.patch(`/accounts/gst/itc/${id}/claim`),
  getITCSummary: (month, year) => api.get('/accounts/gst/itc/summary', { params: { month, year } }),
  
  // GST Returns
  getGSTR1Summary: (month, year) => api.get('/accounts/gst/gstr1/summary', { params: { month, year } }),
  getGSTR3BSummary: (month, year) => api.get('/accounts/gst/gstr3b/summary', { params: { month, year } }),
  
  // TDS/TCS
  getTDSTCSEntries: (params) => api.get('/accounts/gst/tds-tcs', { params }),
  createTDSTCSEntry: (data) => api.post('/accounts/gst/tds-tcs', data),
  markTDSDeposited: (id, challanNumber, depositDate) => 
    api.patch(`/accounts/gst/tds-tcs/${id}/deposit`, null, { params: { challan_number: challanNumber, deposit_date: depositDate } }),
  
  // E-Way Bills
  getEWayBills: (params) => api.get('/accounts/gst/eway-bills', { params }),
  createEWayBill: (data) => api.post('/accounts/gst/eway-bills', data),
  generateEWayBill: (id) => api.post(`/accounts/gst/eway-bills/${id}/generate`),
  
  // AI Suggestions
  getAISuggestions: () => api.get('/accounts/gst/ai/suggestions'),
  getLiabilityForecast: (monthsAhead = 3) => api.get('/accounts/gst/ai/liability-forecast', { params: { months_ahead: monthsAhead } }),
  
  // Detailed Reports
  getGSTR1Detailed: (month, year) => api.get('/accounts/gst/gstr1/detailed', { params: { month, year } }),
  getGSTR3BDetailed: (month, year) => api.get('/accounts/gst/gstr3b/detailed', { params: { month, year } }),
  getITCReconciliation: (month, year) => api.get('/accounts/gst/itc/reconciliation', { params: { month, year } }),
  getEWayBillSummary: () => api.get('/accounts/gst/eway-bills/summary'),
  getDashboardStats: () => api.get('/accounts/gst/dashboard/stats'),
};

// Performance Management API
export const performanceAPI = {
  // Goals/OKRs
  getGoals: (params) => api.get('/performance/goals', { params }),
  getMyGoals: () => api.get('/performance/goals', { params: { my_goals: true } }),
  getTeamGoals: () => api.get('/performance/goals', { params: { my_team: true } }),
  getGoal: (id) => api.get(`/performance/goals/${id}`),
  createGoal: (data) => api.post('/performance/goals', data),
  updateGoal: (id, data) => api.put(`/performance/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`/performance/goals/${id}`),
  submitGoal: (id) => api.post(`/performance/goals/${id}/submit`),
  approveGoal: (id) => api.post(`/performance/goals/${id}/approve`),
  rejectGoal: (id, reason) => api.post(`/performance/goals/${id}/reject`, null, { params: { reason } }),
  updateKeyResultProgress: (goalId, krId, currentValue) => 
    api.put(`/performance/goals/${goalId}/key-results/${krId}/progress`, null, { params: { current_value: currentValue } }),
  
  // Review Cycles
  getCycles: (params) => api.get('/performance/cycles', { params }),
  getCurrentCycle: () => api.get('/performance/cycles/current'),
  createCycle: (data) => api.post('/performance/cycles', data),
  updateCycle: (id, data) => api.put(`/performance/cycles/${id}`, null, { params: data }),
  initiateReviews: (cycleId) => api.post(`/performance/cycles/${cycleId}/initiate-reviews`),
  
  // Performance Reviews
  getReviews: (params) => api.get('/performance/reviews', { params }),
  getMyReview: () => api.get('/performance/reviews', { params: { my_review: true } }),
  getTeamReviews: () => api.get('/performance/reviews', { params: { my_team: true } }),
  getReview: (id) => api.get(`/performance/reviews/${id}`),
  getMyCurrentReview: () => api.get('/performance/reviews/my/current'),
  submitSelfReview: (id, data) => api.post(`/performance/reviews/${id}/self-review`, data),
  submitManagerReview: (id, data) => api.post(`/performance/reviews/${id}/manager-review`, data),
  submitHRReview: (id, data) => api.post(`/performance/reviews/${id}/hr-review`, data),
  
  // Dashboard & Analytics
  getSummary: (cycleId) => api.get('/performance/dashboard/summary', { params: { cycle_id: cycleId } }),
  getMyTeamStats: (cycleId) => api.get('/performance/dashboard/my-team-stats', { params: { cycle_id: cycleId } }),
  getRatingDistribution: (cycleId, department) => api.get('/performance/analytics/rating-distribution', { params: { cycle_id: cycleId, department } }),
};

// Purchase Order API
export const purchaseOrderAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getSummary: () => api.get('/purchase-orders/summary'),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  submit: (id) => api.post(`/purchase-orders/${id}/submit`),
  approve: (id, data) => api.post(`/purchase-orders/${id}/approve`, data),
  markSent: (id) => api.post(`/purchase-orders/${id}/mark-sent`),
  acknowledge: (id) => api.post(`/purchase-orders/${id}/acknowledge`),
  complete: (id) => api.post(`/purchase-orders/${id}/complete`),
  cancel: (id, reason) => api.post(`/purchase-orders/${id}/cancel`, null, { params: { reason } }),
  downloadPDF: (id) => api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
};

// Celebrations API (Birthday & Anniversary)
export const celebrationsAPI = {
  // Templates
  getTemplates: (celebrationType, activeOnly = true) => api.get('/celebrations/templates', { params: { celebration_type: celebrationType, active_only: activeOnly } }),
  createTemplate: (data) => api.post('/celebrations/templates', data),
  updateTemplate: (id, data) => api.put(`/celebrations/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/celebrations/templates/${id}`),
  initializeDefaults: () => api.post('/celebrations/templates/initialize-defaults'),
  
  // Settings
  getSettings: () => api.get('/celebrations/settings'),
  updateSettings: (data) => api.put('/celebrations/settings', data),
  
  // Upcoming Celebrations
  getUpcoming: (daysAhead = 30, celebrationType) => api.get('/celebrations/upcoming', { params: { days_ahead: daysAhead, celebration_type: celebrationType } }),
  getToday: () => api.get('/celebrations/today'),
  getStats: () => api.get('/celebrations/stats'),
  
  // Logs
  getLogs: (params) => api.get('/celebrations/logs', { params }),
  
  // Actions
  sendWish: (employeeId, celebrationType, templateId) => api.post(`/celebrations/send-wish/${employeeId}`, null, { params: { celebration_type: celebrationType, template_id: templateId } }),
  sendReminders: (daysAhead = 1) => api.post('/celebrations/send-reminders', null, { params: { days_ahead: daysAhead } }),
};

// Petty Cash API
export const pettyCashAPI = {
  // Config & Categories
  getConfig: () => api.get('/petty-cash/config'),
  updateConfig: (data) => api.put('/petty-cash/config', data),
  createCategory: (data) => api.post('/petty-cash/categories', data),
  updateCategory: (id, data) => api.put(`/petty-cash/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/petty-cash/categories/${id}`),
  
  // Expenses
  getExpenses: (params) => api.get('/petty-cash/expenses', { params }),
  getExpense: (id) => api.get(`/petty-cash/expenses/${id}`),
  createExpense: (data) => api.post('/petty-cash/expenses', data),
  updateExpense: (id, data) => api.put(`/petty-cash/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/petty-cash/expenses/${id}`),
  approveExpense: (id, remarks = '') => api.post(`/petty-cash/expenses/${id}/approve`, null, { params: { remarks } }),
  rejectExpense: (id, reason) => api.post(`/petty-cash/expenses/${id}/reject`, null, { params: { reason } }),
  
  // Cash Requests
  getCashRequests: (params) => api.get('/petty-cash/cash-requests', { params }),
  createCashRequest: (data) => api.post('/petty-cash/cash-requests', data),
  approveCashRequest: (id, disbursementMode, reference, remarks = '') => api.post(`/petty-cash/cash-requests/${id}/approve`, null, { params: { disbursement_mode: disbursementMode, disbursement_reference: reference, remarks } }),
  rejectCashRequest: (id, reason) => api.post(`/petty-cash/cash-requests/${id}/reject`, null, { params: { reason } }),
  
  // Dashboard & Analytics
  getDashboard: (period = 'month') => api.get('/petty-cash/dashboard', { params: { period } }),
  getReconciliation: (period = 'week') => api.get('/petty-cash/reconciliation', { params: { period } }),
  closeReconciliation: (period, notes) => api.post('/petty-cash/reconciliation/close', null, { params: { period, notes } }),
  getAIInsights: () => api.get('/petty-cash/ai-insights'),
  
  // Email-based approval (public, no auth)
  verifyEmailAction: (token) => api.get('/petty-cash/email-action/verify', { params: { token } }),
  executeEmailAction: (token, remarks = '') => api.post('/petty-cash/email-action/execute', null, { params: { token, remarks } }),
};

// System Settings API
export const systemSettingsAPI = {
  // Organization settings
  getOrganization: () => api.get('/settings/organization'),
  updateOrganization: (data) => api.put('/settings/organization', data),
  
  // Email domain settings
  getEmailDomains: () => api.get('/settings/email-domains'),
  updateEmailDomains: (data) => api.put('/settings/email-domains', data),
  
  // Test data cleanup
  cleanupTestData: (dryRun = true) => api.post('/settings/cleanup/test-data', null, { params: { dry_run: dryRun } }),
  
  // Backup & Restore
  getBackupStatus: () => api.get('/settings/backup/status'),
  exportBackup: () => api.get('/settings/backup/export'),
  exportCollection: (collectionName) => api.get(`/settings/backup/export/${collectionName}`),
  restoreBackup: (backupData, mode = 'merge') => api.post('/settings/backup/restore', backupData, { params: { mode } }),
  restoreCollection: (collectionName, data, mode = 'merge') => api.post(`/settings/backup/restore/${collectionName}`, data, { params: { mode } }),
  
  // Daily Digest
  previewDigest: () => api.get('/settings/digest/preview'),
  sendDigest: () => api.post('/settings/digest/send'),
  sendDigestToMe: () => api.post('/settings/digest/send-to-me'),
  
  // Document Expiry Alerts
  getDocumentExpirySummary: () => api.get('/settings/document-expiry/summary'),
  getExpiringDocuments: (days = 30) => api.get('/settings/document-expiry/expiring', { params: { days } }),
  getExpiredDocuments: () => api.get('/settings/document-expiry/expired'),
  sendDocumentExpiryAlert: () => api.post('/settings/document-expiry/send-alert'),
  notifyEmployees: (days = 14) => api.post('/settings/document-expiry/notify-employees', null, { params: { days } }),
  notifyAll: () => api.post('/settings/document-expiry/notify-all'),
};

// Data Export API
export const exportAPI = {
  // Check if user has export permission
  checkPermission: () => api.get('/export/check-permission'),
  
  // Export functions (require data_export RBAC permission)
  employees: () => api.get('/export/employees', { responseType: 'blob' }),
  leads: (status) => api.get('/export/leads', { params: { status }, responseType: 'blob' }),
  leaves: (year) => api.get('/export/leaves', { params: { year }, responseType: 'blob' }),
  expenses: (status) => api.get('/export/expenses', { params: { status }, responseType: 'blob' }),
  assets: () => api.get('/export/assets', { responseType: 'blob' }),
  attendance: (month) => api.get('/export/attendance', { params: { month }, responseType: 'blob' }),
  timesheets: (weekStart) => api.get('/export/timesheets', { params: { week_start: weekStart }, responseType: 'blob' }),
  invoices: (status) => api.get('/export/invoices', { params: { status }, responseType: 'blob' }),
  purchaseOrders: (status) => api.get('/export/purchase-orders', { params: { status }, responseType: 'blob' }),
  pettyCash: () => api.get('/export/petty-cash', { responseType: 'blob' }),
};

// Employee Documents API
export const employeeDocumentsAPI = {
  getTypes: () => api.get('/employee-documents/types'),
  getDocuments: (employeeId) => api.get(`/employee-documents/${employeeId}`),
  uploadDocument: (employeeId, formData) => api.post(`/employee-documents/${employeeId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadDocument: (employeeId, documentId) => api.get(`/employee-documents/${employeeId}/${documentId}/download`, { responseType: 'blob' }),
  deleteDocument: (employeeId, documentId) => api.delete(`/employee-documents/${employeeId}/${documentId}`),
  verifyDocument: (employeeId, documentId) => api.patch(`/employee-documents/${employeeId}/${documentId}/verify`),
  getExpiringDocuments: (days = 30) => api.get('/employee-documents/expiring/all', { params: { days } }),
  getDocumentStats: () => api.get('/employee-documents/stats/overview'),
};

// What's New API (Release Notes / Changelog)
export const whatsNewAPI = {
  getReleases: (params) => api.get('/whats-new', { params }),
  getLatest: () => api.get('/whats-new/latest'),
  getUnread: () => api.get('/whats-new/unread'),
  getRelease: (id) => api.get(`/whats-new/${id}`),
  createRelease: (data) => api.post('/whats-new', data),
  updateRelease: (id, data) => api.put(`/whats-new/${id}`, data),
  deleteRelease: (id) => api.delete(`/whats-new/${id}`),
  sendNotifications: (id) => api.post(`/whats-new/${id}/notify`),
};

// Escalation Reminders API
export const escalationAPI = {
  getSummary: () => api.get('/escalation-reminders/summary'),
  getPending: (params) => api.get('/escalation-reminders/pending', { params }),
  getDisciplinaryLogs: (params) => api.get('/escalation-reminders/disciplinary-logs', { params }),
  getMyReminders: () => api.get('/escalation-reminders/my-reminders'),
  resolve: (data) => api.post('/escalation-reminders/resolve', data),
  runCheck: () => api.post('/escalation-reminders/run-check'),
  sendReminder: (reminderId) => api.post(`/escalation-reminders/send-reminder/${reminderId}`),
};

// Project Costing API
export const projectCostingAPI = {
  getProjects: (status) => api.get('/project-costing/projects', { params: { status } }),
  getProject: (id) => api.get(`/project-costing/projects/${id}`),
  createProject: (data) => api.post('/project-costing/projects', data),
  updateProject: (id, data) => api.put(`/project-costing/projects/${id}`, data),
  addCostEntry: (data) => api.post('/project-costing/cost-entries', data),
  deleteCostEntry: (id) => api.delete(`/project-costing/cost-entries/${id}`),
  getSummary: () => api.get('/project-costing/summary'),
};

export default api;
